import React, { useState } from "react";
import { Box, Popover, Typography, ListItemText, Tooltip } from "@mui/material";
import CustomDropdown from "../Common/CustomDropdown";
import { capitalizeFirstLetter, formatClientDomain } from "../../utils/utils";
import DropdownBox from "../Common/DropdownBox";
import { Client } from "../../utils/types";


interface AccountSelectorProps {
  value: string | null;
  onChange: (clientOrgId: string | null, clientName: string | null) => void;
  clientList: Client[];
  icon: string;
  useDropdownBox?: boolean;
  error?: boolean;
  disabled?: boolean;
  isPreview?: boolean;
  tooltipTitle?: string;
  id: string;
  activeDropdownId: string | null;
  setActiveDropdownId: (id: string | null) => void;
}

const AccountSelector: React.FC<AccountSelectorProps> = ({
  value,
  onChange,
  clientList,
  icon,
  useDropdownBox = false,
  error = false,
  disabled = false,
  isPreview = false,
  tooltipTitle,
  id,
  activeDropdownId,
  setActiveDropdownId,
}) => {
  const isOpen = activeDropdownId === id;
  const [localAnchorEl, setLocalAnchorEl] = useState<HTMLElement | null>(null);


  const selectedClient =
    clientList.find((c) => c.clientName === value) || null;
  const label = selectedClient
    ? selectedClient.clientName
    : value
      ? formatClientDomain(value)
      : null;

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled || isPreview) return;
    setLocalAnchorEl(e.currentTarget);
    setActiveDropdownId(id); // 👈 closes others
  };

  const handleClose = () => {
    setActiveDropdownId(null);
    setLocalAnchorEl(null);
  };


  const elementToRender = label ? (
    <Typography
      sx={{
        fontSize: "12px",
        color: "#33333B",
        marginLeft: "6px",
        cursor: "pointer",
      }}
      onClick={handleClick}
    >
      {formatClientDomain(label)}
    </Typography>
  ) : (
    <img
      src={icon}
      alt="Account"
      className="inline-chip-icon"
      style={{ cursor: "pointer" }}
      onClick={handleClick}
    />
  );

  /** Tooltip wrapper (only rendered if tooltipTitle exists) */
  const wrappedWithTooltip =
    tooltipTitle ? (
      <Tooltip title={tooltipTitle} arrow placement="top">
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          {elementToRender}
        </span>
      </Tooltip>
    ) : (
      elementToRender
    );

  return (
    <>
      {useDropdownBox ? (
        <DropdownBox
          onClick={handleClick}
          error={error}
          disabled={isPreview || disabled}
          className="assignee-box"
        >
          {label ? (
            <Typography
              sx={{
                fontSize: "12px",
                color: "#33333B",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {formatClientDomain(label)}
            </Typography>
          ) : (
            <span className="priority-label">Select</span>
          )}
        </DropdownBox>
      ) : (
        wrappedWithTooltip   // <-- only render ONE trigger
      )}
      <Popover
        open={isOpen && Boolean(localAnchorEl)}
        anchorEl={localAnchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <CustomDropdown<Client>
          items={clientList}
          value={selectedClient}
          onChange={(client) => {
            const selected = Array.isArray(client) ? client[0] : client;
            if (selected) {
              onChange(selected.clientOrgId, selected.clientName);
            } else {
              onChange(null, null);
            }
            handleClose();
          }}
          placeholder="Search by account"
          showSearch
          maxHeight={220}
          forceShowList={clientList.length <= 5}
          renderItem={(client) => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "26px",
                px: 1,
                cursor: "pointer",
              }}
            >
              <ListItemText
                primary={capitalizeFirstLetter(client.clientName)}
                primaryTypographyProps={{ noWrap: true, fontSize: "13px" }}
              />
            </Box>
          )}
          filterFn={(client, search) =>
            client.clientName.toLowerCase().includes(search.toLowerCase())
          }
        />
      </Popover>
    </>
  );
};
export default AccountSelector;
